"use client";

import React from "react";
import { usePathname } from "next/navigation";
import type { MenuNode } from "./NavigationMenuList";

/**
 * Horizontal flat navigation bar — used as a department sub-nav placed
 * under the hero (the `navigationMenu` section with
 * `orientation: 'horizontal'`). Renders the top-level menu items as a
 * single scrollable row, highlights the active item with the theme's
 * primary color, and exposes immediate children as a hover dropdown.
 *
 * The data fetch + dept scoping live in the server-component sibling
 * (`NavigationMenuSection.tsx`); this takes the resolved tree as a prop.
 */

interface Props {
  tree: MenuNode[];
  showIcons: boolean;
  currentLanguage: "en" | "mm";
}

function resolveHref(node: MenuNode): string {
  return node.url || (node.slug ? `/${node.slug}` : "#");
}

function label(node: MenuNode, language: "en" | "mm"): string {
  return node.title?.[language] || node.title?.en || node.title?.mm || "";
}

export function NavigationMenuBar({ tree, showIcons, currentLanguage }: Props) {
  const pathname = (usePathname() || "/").replace(/\/$/, "");

  const isActive = (node: MenuNode): boolean => {
    const href = resolveHref(node).replace(/\/$/, "");
    if (href && href !== "#" && href === pathname) return true;
    // A parent is "active" when the current route is one of its children.
    return (node.children || []).some(
      (c) => resolveHref(c).replace(/\/$/, "") === pathname,
    );
  };

  return (
    <div className="border-y border-primary/20 bg-background/60">
      <ul className="container mx-auto flex items-center gap-1 overflow-x-auto px-4">
        {tree.map((node) => {
          const href = resolveHref(node);
          const active = isActive(node);
          const hasChildren =
            Array.isArray(node.children) && node.children.length > 0;
          return (
            <li key={node._id} className="group relative shrink-0">
              <a
                href={href}
                target={node.openInNewTab ? "_blank" : undefined}
                rel={node.openInNewTab ? "noopener noreferrer" : undefined}
                aria-current={active ? "page" : undefined}
                className={[
                  "flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors",
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-foreground/70 hover:border-primary/40 hover:text-primary",
                ].join(" ")}
              >
                {showIcons && node.icon && (
                  <span aria-hidden="true">{node.icon}</span>
                )}
                {label(node, currentLanguage)}
              </a>

              {hasChildren && (
                <ul className="invisible absolute left-0 top-full z-30 min-w-[12rem] rounded-md border border-border bg-popover py-1 opacity-0 shadow-md transition-opacity group-hover:visible group-hover:opacity-100">
                  {node.children!.map((child) => (
                    <li key={child._id}>
                      <a
                        href={resolveHref(child)}
                        target={child.openInNewTab ? "_blank" : undefined}
                        rel={
                          child.openInNewTab ? "noopener noreferrer" : undefined
                        }
                        className="block px-3 py-2 text-sm text-foreground/80 hover:bg-primary/10 hover:text-primary"
                      >
                        {label(child, currentLanguage)}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default NavigationMenuBar;
